import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Copy, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useWalletKit } from "@web3jskit/walletkit";
import { MaxUint256, parseUnits } from "ethers";
import {
  createDepositOrder,
  getDepositOrderDetail,
  type CreateDepositOrderResult,
  type DepositOrder,
} from "@/api";
import { useDepositBlockchainsStore } from "@/stores/modules/depositBlockchains";
import { depositInterface, erc20Interface } from "@/abi";
import { formatUsdtBalance } from "@/utils";
import { switchOrAddChain } from "@/utils/evmChains";
import { isPositiveUsdt } from "@/utils/money";
import {
  WalletError,
  classifyWalletError,
  getWalletErrorMessageKey,
  probeWalletAlive,
  raceWithAbort,
} from "@/utils/walletError";
import {
  NetworkInfoCard,
  NetworkInfoCardSkeleton,
  hasValidChainLogoUrl,
} from "./NetworkInfoCard";

interface RechargeModalProps {
  open: boolean;
  onClose: () => void;
  /** 充值最终成功（链上确认）后回调，用于刷新余额 */
  onSuccess?: () => void;
}

const AMOUNT_PATTERN = /^\d+(\.\d+)?$/;
/** 部分输入校验（允许末尾以 `.` 结尾、空字符串等"输入中"中间态） */
const PARTIAL_AMOUNT_PATTERN = /^\d*\.?\d*$/;
/** 输入框层面允许的最大小数位（产品侧约束，实际精度仍以接口返回的 tokenDecimals 为准做兜底） */
const MAX_DECIMALS = 4;
/** uint256 最大值的十进制字符串形式（78 位整数） */
const MAX_AMOUNT_STR = MaxUint256.toString();

/**
 * 把 `value` 作为十进制数 X 与 MaxUint256 比较：
 * - 整数部分 > MaxUint256 → 夹到 {@link MAX_AMOUNT_STR}
 * - 整数部分 = MaxUint256 且小数部分含非零位 → 夹到 {@link MAX_AMOUNT_STR}
 * - 其他情况原样返回（含中间态如 `""`、`"0."`）
 */
function clampToMaxUint256(value: string): string {
  if (!AMOUNT_PATTERN.test(value)) return value;
  const [intPart, fracPart = ""] = value.split(".");
  let intBig: bigint;
  try {
    intBig = BigInt(intPart);
  } catch {
    return value;
  }
  if (intBig > MaxUint256) return MAX_AMOUNT_STR;
  if (intBig === MaxUint256 && /[1-9]/.test(fracPart)) return MAX_AMOUNT_STR;
  return value;
}

/**
 * 校验/规范化用户在金额输入框中的"输入中"字符串：
 * - 拒绝非法字符（含 `e`、`+`、`-`、空格、中文、第二个 `.` 等）
 * - `".5"` → `"0.5"`
 * - 去前导零（`"0002"` → `"2"`，`"0.5"` 保留）
 * - 小数位超过 {@link MAX_DECIMALS} 时整体拒绝（超出的字符不会写入 state）
 * - 数值超过 uint256 最大值时，夹到 MaxUint256（{@link MAX_AMOUNT_STR}）
 *
 * 返回 `null` 表示原输入非法、应丢弃；返回字符串表示规范化后的值。
 */
function sanitizeAmount(raw: string): string | null {
  if (raw === "") return "";
  if (!PARTIAL_AMOUNT_PATTERN.test(raw)) return null;
  let next = raw;
  if (next.startsWith(".")) next = "0" + next;
  if (/^0\d/.test(next)) next = next.replace(/^0+/, "");
  if (countDecimals(next) > MAX_DECIMALS) return null;
  next = clampToMaxUint256(next);
  return next;
}

/** 字符串中小数点后的位数（无小数点返回 0） */
function countDecimals(s: string): number {
  const dot = s.indexOf(".");
  return dot < 0 ? 0 : s.length - dot - 1;
}

function isPartialAmount(s: string): boolean {
  if (s === "") return true;
  if (!PARTIAL_AMOUNT_PATTERN.test(s)) return false;
  return countDecimals(s) <= MAX_DECIMALS;
}
/** approve 交易上链轮询间隔（毫秒） */
const RECEIPT_POLL_INTERVAL_MS = 3_000;
/** approve 交易上链轮询最大次数（60s） */
const RECEIPT_POLL_MAX_TIMES = 20;
/** 订单状态轮询间隔（毫秒） */
const POLL_INTERVAL_MS = 5_000;
/** 订单状态轮询最大次数（5 分钟） */
const POLL_MAX_TIMES = 60;

type ViewMode = "form" | "tracking";
type SigStep = "idle" | "approve" | "deposit";

/**
 * 弹窗内部的 order 状态：以创建响应 {@link CreateDepositOrderResult} 为底
 * （含 contractAddress / tokenDecimals），但 status 需要能随轮询从 `'pending'`
 * 变为 `'paid' | 'expired'`，所以此处把 status 扩宽为 {@link DepositOrder} 的 status。
 */
type OrderState = Omit<CreateDepositOrderResult, "status"> & {
  status: DepositOrder["status"];
};

const DEPOSIT_FLOW = "[DepositFlow]";

/** 日志용合约地址缩略 */
function shortHexAddr(addr: string): string {
  if (!addr || addr.length < 12) return addr || "—";
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/**
 * 缩略地址：前 6 位 + ... + 后 4 位
 * 空值或过短时返回 `—`，避免 UI 出现 0 宽度空白。
 */
function shortenAddress(address: string | undefined | null): string {
  if (!address) return "—";
  if (address.length < 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/**
 * 通过 eth_getTransactionReceipt 轮询等待交易上链。
 * 成功返回 receipt，失败 / 超时抛错。
 */
async function waitForReceipt(
  provider: { request: (args: { method: string; params?: unknown[] }) => Promise<unknown> },
  txHash: string,
  signal: AbortSignal
): Promise<{ status: string }> {
  for (let i = 0; i < RECEIPT_POLL_MAX_TIMES; i += 1) {
    if (signal.aborted) throw new WalletError("cancelled");
    const receipt = (await raceWithAbort(
      provider.request({
        method: "eth_getTransactionReceipt",
        params: [txHash],
      }),
      signal
    )) as { status: string } | null;

    if (receipt) {
      if (receipt.status === "0x1") return receipt;
      throw new Error(`Transaction reverted: ${txHash}`);
    }

    await new Promise((resolve) => setTimeout(resolve, RECEIPT_POLL_INTERVAL_MS));
  }
  throw new Error(`Transaction receipt timeout: ${txHash}`);
}

export function RechargeModal({ open, onClose, onSuccess }: RechargeModalProps) {
  const { t } = useTranslation();
  const { provider } = useWalletKit();

  const [amount, setAmount] = useState("");
  const blockchains = useDepositBlockchainsStore((s) => s.blockchains);
  const chainsStatus = useDepositBlockchainsStore((s) => s.status);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sigStep, setSigStep] = useState<SigStep>("idle");
  const [view, setView] = useState<ViewMode>("form");
  const [order, setOrder] = useState<OrderState | null>(null);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [copied, setCopied] = useState(false);

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Lets the user abandon a wallet step that would otherwise block forever
  // (e.g. signature popup never arrives because the extension was disabled).
  const abortRef = useRef<AbortController | null>(null);

  const selectedChain = blockchains[0] ?? null;
  const isLoadingChains = open && chainsStatus === "loading";

  const chainConfigId = useMemo(() => {
    if (!selectedChain) return null;
    const id = Number(selectedChain.id);
    return Number.isFinite(id) ? id : null;
  }, [selectedChain]);

  // Decimal-safe positivity check: avoids float drift / oversize-string issues
  // around 0 (e.g. "0.0000000001" or "1e18" stays accurate via Decimal).
  const isAmountValid = AMOUNT_PATTERN.test(amount) && isPositiveUsdt(amount);
  const isSignatureLoading = sigStep !== "idle";

  const handleAmountChange = useCallback((raw: string) => {
    const next = sanitizeAmount(raw);
    if (next === null) return;
    setAmount(next);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollCountRef.current = 0;
  }, []);

  const stopTick = useCallback(() => {
    if (tickTimerRef.current) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
  }, []);

  // 弹窗关闭时重置；打开时确保链列表已拉取（store 已成功则不会重复请求）
  useEffect(() => {
    if (!open) {
      setAmount("");
      setIsSubmitting(false);
      setSigStep("idle");
      setView("form");
      setOrder(null);
      setCopied(false);
      stopPolling();
      stopTick();
      return;
    }

    void useDepositBlockchainsStore.getState().refreshBlockchains();
  }, [open, stopPolling, stopTick]);

  // 卸载或关闭时清理定时器
  useEffect(() => {
    return () => {
      stopPolling();
      stopTick();
    };
  }, [stopPolling, stopTick]);

  // 启动订单状态轮询
  const startPolling = useCallback(
    (orderId: string) => {
      stopPolling();
      pollCountRef.current = 0;

      pollTimerRef.current = setInterval(async () => {
        pollCountRef.current += 1;

        if (pollCountRef.current > POLL_MAX_TIMES) {
          stopPolling();
          return;
        }

        try {
          // yaml 中 GET /deposit/orders/{id} 的 path 参数已改为 integer
          const orderIdNum = Number(orderId);
          if (!Number.isFinite(orderIdNum)) {
            stopPolling();
            return;
          }
          const res = await getDepositOrderDetail(orderIdNum);
          if (!res.ok) return;
          const detail = res.data;
          // merge 而非覆盖：DepositOrder 不含 contractAddress / tokenDecimals / orderId 等创建期字段，
          // 必须以 createDepositOrder 首次返回的 CreateDepositOrderResult 为底；
          // prev 为 null 的分支仅用于类型安全（实际 polling 开始时 prev 一定存在）。
          setOrder((prev) => (prev ? { ...prev, ...detail } : prev));

          if (detail.status === "paid") {
            stopPolling();
            toast.success(t("dashboard.rechargeModal.toastRechargePaid"));
            onSuccess?.();
            onClose();
          } else if (detail.status === "expired") {
            stopPolling();
            toast.warning(t("dashboard.rechargeModal.toastOrderExpired"));
            setView("form");
            setOrder(null);
          }
        } catch (error) {
          console.error("[RechargeModal] poll order detail error:", error);
        }
      }, POLL_INTERVAL_MS);
    },
    [onClose, onSuccess, stopPolling, t]
  );

  // 进入跟踪视图时启动倒计时刷新
  useEffect(() => {
    if (view !== "tracking") {
      stopTick();
      return;
    }
    setNow(Math.floor(Date.now() / 1000));
    tickTimerRef.current = setInterval(() => {
      setNow(Math.floor(Date.now() / 1000));
    }, 1_000);
    return () => stopTick();
  }, [view, stopTick]);

  if (!open) return null;

  const handleClose = () => {
    if (isSubmitting || isSignatureLoading) return;
    stopPolling();
    stopTick();
    onClose();
  };

  // Escape hatch for a wallet step stuck in loading (e.g. signature popup never
  // arrives because the extension was disabled mid-flow): aborts the in-flight
  // request so the catch block resets the UI and the user regains control.
  const handleCancelSignature = () => {
    abortRef.current?.abort();
  };

  const handleCopyAddress = async () => {
    if (!order?.contractAddress) return;
    try {
      await navigator.clipboard.writeText(order.contractAddress);
      setCopied(true);
      toast.success(t("dashboard.rechargeModal.tracking.copySuccess"));
      setTimeout(() => setCopied(false), 1_500);
    } catch (err) {
      console.error("[RechargeModal] copy address error:", err);
    }
  };

  const handleConfirm = async () => {
    if (isSubmitting) return;

    if (!isAmountValid) {
      toast.error(t("dashboard.toastAmountInvalid"));
      return;
    }

    if (!selectedChain || chainConfigId === null) {
      toast.error(t("dashboard.toastRechargePlanFail"));
      return;
    }

    if (!provider) {
      toast.error(t("wallet.errorNoProvider"));
      return;
    }

    setIsSubmitting(true);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // Fail fast if the wallet provider is dead (disabled / invalidated
      // extension) before showing any blocking signature UI.
      await probeWalletAlive(provider);

      // 1. 创建订单
      const orderRes = await createDepositOrder({
        amount,
        chainConfigId,
      });

      if (!orderRes.ok) {
        toast.error(orderRes.error.message || t("dashboard.toastRechargeFail"));
        return;
      }

      const orderData = orderRes.data;
      const {
        orderNo,
        tokenAddress,
        tokenDecimals,
        contractAddress,
        chainId,
      } = orderData;

      if (!orderNo || !tokenAddress || !contractAddress) {
        toast.error(t("dashboard.toastRechargeFail"));
        return;
      }

      if (!Number.isInteger(tokenDecimals) || tokenDecimals < 0) {
        console.warn(`${DEPOSIT_FLOW} invalid tokenDecimals`, tokenDecimals);
        toast.error(t("dashboard.toastRechargeFail"));
        return;
      }

      // 1.5 按接口返回的 tokenDecimals 做二次兜底校验。
      // 输入框已在 UI 层硬限制到 MAX_DECIMALS=4 位小数，正常不会触发；
      // 这里仅防御后端返回的 tokenDecimals 小于 4 的场景（理论罕见），
      // 避免 parseUnits 抛 ethers 内部错（"underflow" / "fractional component exceeds decimals"）。
      if (countDecimals(amount) > tokenDecimals) {
        toast.error(
          t("dashboard.rechargeModal.toastAmountDecimalsExceeded", {
            decimals: tokenDecimals,
          })
        );
        return;
      }

      let numericChainId: number | null = null;
      if (
        chainId !== undefined &&
        chainId !== null &&
        String(chainId) !== ""
      ) {
        const n = Number(chainId);
        if (!Number.isFinite(n)) {
          console.warn(`${DEPOSIT_FLOW} invalid chainId from order`, chainId);
          toast.error(t("dashboard.toastRechargeFail"));
          return;
        }
        numericChainId = n;
      }

      console.info(`${DEPOSIT_FLOW} order created`, {
        orderId: orderData.orderId,
        orderNo,
        chainId: numericChainId,
        tokenDecimals,
        contract: shortHexAddr(contractAddress),
        token: shortHexAddr(tokenAddress),
      });

      // 2. 钱包账户
      const accounts = (await provider.request({
        method: "eth_requestAccounts",
      })) as string[];
      if (!accounts || accounts.length === 0) {
        toast.error(t("dashboard.toastWalletNotReady"));
        return;
      }
      const from = accounts[0];

      // 3. 链校验 + 切换（失败且 4902 时自动 wallet_addEthereumChain）
      if (numericChainId !== null) {
        console.info(`${DEPOSIT_FLOW} switchOrAddChain →`, numericChainId);
        await switchOrAddChain(provider, numericChainId, {
          chainName: selectedChain.chainName,
          iconUrl: selectedChain.chainLogoUrl,
        });
        console.info(`${DEPOSIT_FLOW} switchOrAddChain ok`);
      }

      // 4. allowance 检查
      const amountUnits = parseUnits(amount, tokenDecimals);
      const allowanceData = erc20Interface.encodeFunctionData("allowance", [
        from,
        contractAddress,
      ]);
      const rawAllowance = (await provider.request({
        method: "eth_call",
        params: [{ to: tokenAddress, data: allowanceData }, "latest"],
      })) as string;
      const allowanceResult = erc20Interface.decodeFunctionResult(
        "allowance",
        rawAllowance
      );
      const currentAllowance = allowanceResult[0] as bigint;

      const needApprove = currentAllowance < amountUnits;
      console.info(`${DEPOSIT_FLOW} allowance`, {
        currentAllowance: currentAllowance.toString(),
        amountUnits: amountUnits.toString(),
        needApprove,
      });

      // 5. 不够则 approve(MaxUint256)
      if (needApprove) {
        setSigStep("approve");
        const approveData = erc20Interface.encodeFunctionData("approve", [
          contractAddress,
          MaxUint256,
        ]);
        console.info(`${DEPOSIT_FLOW} eth_sendTransaction approve →`, {
          to: shortHexAddr(tokenAddress),
        });
        const approveTxHash = (await raceWithAbort(
          provider.request({
            method: "eth_sendTransaction",
            params: [
              {
                from,
                to: tokenAddress,
                data: approveData,
                value: "0x0",
              },
            ],
          }),
          controller.signal
        )) as string;
        console.info(`${DEPOSIT_FLOW} approve tx broadcast`, { approveTxHash });
        await waitForReceipt(provider, approveTxHash, controller.signal);
        console.info(`${DEPOSIT_FLOW} approve receipt ok`);
      }

      // 6. deposit(orderNo, token, amount)
      setSigStep("deposit");
      const depositData = depositInterface.encodeFunctionData("deposit", [
        orderNo,
        tokenAddress,
        amountUnits,
      ]);
      console.info(`${DEPOSIT_FLOW} eth_sendTransaction deposit →`, {
        to: shortHexAddr(contractAddress),
      });
      await raceWithAbort(
        provider.request({
          method: "eth_sendTransaction",
          params: [
            {
              from,
              to: contractAddress,
              data: depositData,
              value: "0x0",
            },
          ],
        }),
        controller.signal
      );
      console.info(`${DEPOSIT_FLOW} deposit tx request returned (broadcast)`);

      // 7. 广播成功 → tracking + startPolling
      setSigStep("idle");
      setOrder(orderData);
      setView("tracking");
      toast.success(t("dashboard.rechargeModal.toastRechargeBroadcast"));
      startPolling(orderData.orderId);
    } catch (error) {
      const err = error as { code?: number; message?: string };
      console.error("[RechargeModal] recharge error:", error);
      console.error(`${DEPOSIT_FLOW} failed`, {
        code: err.code,
        message: err.message,
      });
      setSigStep("idle");

      const code = classifyWalletError(error);

      // 用户点了"取消"逃生：复位即可，保留表单让其重试。
      if (code === "cancelled") {
        toast.info(t("wallet.cancelled"));
        return;
      }
      // 用户在钱包里主动拒绝交易。
      if (code === "userRejected") {
        toast.info(t("dashboard.rechargeModal.toastTxRejected"));
        // 保留 amount，保留表单视图，用户可重新点"确认"
        return;
      }
      // 钱包层错误（provider 失效 / 无响应 / 链未配置）给精确提示；
      // 其它（链上 revert / 后端失败等）沿用充值失败文案。
      toast.error(
        code === "unknown"
          ? t("dashboard.toastRechargeFail")
          : t(getWalletErrorMessageKey(code))
      );
    } finally {
      abortRef.current = null;
      setIsSubmitting(false);
    }
  };

  const expiresIn = order ? Math.max(0, order.expiresAt - now) : 0;
  const expiresMinutes = Math.floor(expiresIn / 60);
  const expiresSeconds = expiresIn % 60;
  const expiresLabel = `${expiresMinutes}:${expiresSeconds
    .toString()
    .padStart(2, "0")}`;

  const chainLogoUrl = selectedChain?.chainLogoUrl;
  const chainName =
    selectedChain?.chainName || t("dashboard.rechargeModal.signature.bscNetwork");

  const signatureDescription =
    sigStep === "approve"
      ? t("dashboard.rechargeModal.signature.approveStep")
      : sigStep === "deposit"
        ? t("dashboard.rechargeModal.signature.depositStep")
        : t("dashboard.rechargeModal.signature.description");

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-slate-50 dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-[#D2F0E1]/5 max-w-md w-full p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {view === "form"
                ? t("dashboard.rechargeModal.title")
                : t("dashboard.rechargeModal.tracking.title")}
            </h3>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting || isSignatureLoading}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {view === "form" && (
            <>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                    {t("dashboard.rechargeModal.amountLabel")}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    pattern="^\d*\.?\d*$"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    onPaste={(e) => {
                      const text = e.clipboardData.getData("text").trim();
                      if (!isPartialAmount(text)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder={t("dashboard.rechargeModal.amountPlaceholder")}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 bg-white dark:bg-[#0E0F11] border border-slate-300 dark:border-[#D2F0E1]/5 rounded-xl focus:ring-2 focus:ring-emerald-400 dark:focus:ring-[#00FFA3]/50 focus:border-emerald-500 dark:focus:border-[#00FFA3] outline-none text-slate-900 dark:text-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                {isLoadingChains || !selectedChain ? (
                  <NetworkInfoCardSkeleton />
                ) : (
                  <NetworkInfoCard
                    name={selectedChain.chainName}
                    logoUrl={selectedChain.chainLogoUrl}
                  />
                )}
              </div>

              <button
                type="button"
                onClick={handleConfirm}
                disabled={isSubmitting || !isAmountValid || isLoadingChains || !selectedChain}
                className="w-full bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] px-4 py-3 rounded-full font-bold hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("dashboard.rechargeModal.confirm", "Confirm & Sign")}
              </button>
            </>
          )}

          {view === "tracking" && order && (
            <div className="space-y-5">
              <div className="flex flex-col items-center justify-center text-center py-2">
                <div className="relative w-14 h-14 mb-4">
                  <div className="absolute inset-0 border-4 border-slate-200 dark:border-[#D2F0E1]/10 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-transparent border-t-emerald-500 dark:border-t-[#00FFA3] rounded-full animate-spin"></div>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                  {t("dashboard.rechargeModal.tracking.note")}
                </p>
              </div>

              <div className="bg-white dark:bg-[#0E0F11] rounded-xl p-4 border border-slate-200 dark:border-[#D2F0E1]/5 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500 dark:text-slate-400">
                    {t("dashboard.rechargeModal.tracking.orderNo")}
                  </span>
                  <span className="font-mono text-xs text-slate-900 dark:text-slate-200">
                    {order.orderNo}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500 dark:text-slate-400">
                    {t("dashboard.rechargeModal.tracking.amount")}
                  </span>
                  <span className="font-bold text-slate-900 dark:text-white">
                    {formatUsdtBalance(order.amount)} {order.tokenSymbol}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500 dark:text-slate-400">
                    {t("dashboard.rechargeModal.tracking.contract")}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyAddress}
                    className="flex items-center gap-1.5 font-mono text-xs text-slate-700 dark:text-slate-200 hover:text-emerald-600 dark:hover:text-[#00FFA3] transition-colors"
                    aria-label={t("dashboard.rechargeModal.tracking.copyAddress")}
                  >
                    {shortenAddress(order.contractAddress)}
                    {copied ? (
                      <Check className="w-3.5 h-3.5 text-emerald-500 dark:text-[#00FFA3]" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500 dark:text-slate-400">
                    {t("dashboard.rechargeModal.tracking.expiresIn")}
                  </span>
                  <span
                    className={`font-mono text-xs ${
                      expiresIn <= 30
                        ? "text-red-500"
                        : "text-slate-700 dark:text-slate-200"
                    }`}
                  >
                    {expiresLabel}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleClose}
                className="w-full bg-slate-100 dark:bg-[#0E0F11] border border-slate-200 dark:border-[#D2F0E1]/10 text-slate-700 dark:text-slate-200 px-4 py-3 rounded-full font-bold hover:bg-slate-200 dark:hover:bg-[#1a1d1f] transition-colors"
              >
                {t("dashboard.rechargeModal.done")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Waiting for Signature Modal */}
      {isSignatureLoading && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-slate-50 dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-[#D2F0E1]/5 max-w-sm w-full p-8 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="relative w-16 h-16 mb-6">
                <div className="absolute inset-0 border-4 border-slate-200 dark:border-[#D2F0E1]/10 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-transparent border-t-emerald-500 dark:border-t-[#00FFA3] rounded-full animate-spin"></div>
              </div>

              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                {t("dashboard.rechargeModal.signature.title")}
              </h3>

              <p className="text-sm text-slate-500 dark:text-[#606566] leading-relaxed mb-4">
                {signatureDescription}
              </p>

              <div className="flex items-center gap-2 bg-white dark:bg-[#0E0F11] rounded-full px-4 py-2 border border-slate-200 dark:border-[#D2F0E1]/5">
                {hasValidChainLogoUrl(chainLogoUrl) ? (
                  <img
                    src={chainLogoUrl!.trim()}
                    alt={chainName}
                    className="w-5 h-5 rounded-full object-cover ring-1 ring-slate-200/80 dark:ring-slate-600/80"
                  />
                ) : (
                  <div
                    className="h-5 w-5 shrink-0 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse ring-1 ring-slate-200/80 dark:ring-slate-600/80"
                    aria-hidden
                  />
                )}
                <span className="text-sm font-bold text-slate-900 dark:text-slate-200">
                  {chainName}
                </span>
              </div>

              <button
                type="button"
                onClick={handleCancelSignature}
                className="mt-6 w-full bg-slate-100 dark:bg-[#0E0F11] border border-slate-200 dark:border-[#D2F0E1]/10 text-slate-700 dark:text-slate-200 px-4 py-2.5 rounded-full font-bold hover:bg-slate-200 dark:hover:bg-[#1a1d1f] transition-colors text-sm"
              >
                {t("wallet.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
